import { http } from './http'
import { auth } from './auth'
import {
  AgendaNoteSchema,
  EntitySchema,
  IndividualSchema,
  ItemSchema,
  ItemVariableSchema,
  RenderedVersionSchema,
  SignatureSchema,
  SignaturePageSchema,
  SigningPositionSchema,
  VersionSchema,
  VersionRenderedVariableSchema,
  VersionDetectedVariableSchema,
} from '../schemas'
import { parseWithZod } from '../utils/parse-with-zod'
import z from 'zod'

const ItemResponseSchema = z.object({ item: ItemSchema })
const PullDmsToVersionResponseSchema = z.object({ version: VersionSchema })

const ItemVariableResponseSchema = z.object({
  item_variables: z.array(ItemVariableSchema),
})

const RenderedVersionsResponseSchema = z.object({
  rendered_versions: z.array(RenderedVersionSchema),
})

const VersionRenderedVariablesResponseSchema = z.object({
  version_rendered_variables: z.array(VersionRenderedVariableSchema),
})

const VersionDetectedVariablesResponseSchema = z.object({
  version_detected_variables: z.array(VersionDetectedVariableSchema),
})

/**
 * Confirming detected signatures creates real signatures, and may create the parties and signing
 * positions behind them, so the response carries every record the server touched. It returns `{}`
 * when nothing was confirmed — hence every collection is optional and defaults to empty.
 */
const ConfirmDetectedSignaturesResponseSchema = z.object({
  signatures: z.array(SignatureSchema).default([]),
  signature_pages: z.array(SignaturePageSchema).default([]),
  signing_positions: z.array(SigningPositionSchema).default([]),
  individuals: z.array(IndividualSchema).default([]),
  entities: z.array(EntitySchema).default([]),
})

/**
 * Legacy signature detection returns only the pages it was handed, flagged as detecting. The
 * signatures it works out land asynchronously and reach the client over the deal's live
 * connection. `{}` comes back when the id list was empty, hence the default.
 */
const DetectSignaturesResponseSchema = z.object({
  signature_pages: z.array(SignaturePageSchema).default([]),
})

export const items = {
  getItemVariables: async (itemId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/items/${itemId}/item_variables`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(ItemVariableResponseSchema, result)
    return parsedResult.item_variables
  },

  getRenderedVersions: async (itemId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/items/${itemId}/rendered_versions`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(RenderedVersionsResponseSchema, result)
    return parsedResult.rendered_versions
  },

  getAgendaNotes: async (itemId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/items/${itemId}/agenda_notes`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(z.object({ agenda_notes: z.array(AgendaNoteSchema) }), result)
    return parsedResult.agenda_notes
  },

  getVersionRenderedVariables: async (itemId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/items/${itemId}/version_rendered_variables`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(VersionRenderedVariablesResponseSchema, result)
    return parsedResult.version_rendered_variables
  },

  getVersionDetectedVariables: async (itemId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/items/${itemId}/version_detected_variables`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(VersionDetectedVariablesResponseSchema, result)
    return parsedResult.version_detected_variables
  },

  syncDms: async (itemId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/items/${itemId}/sync_dms`, {}, dealId)
    return parseWithZod(ItemResponseSchema, result).item
  },

  pullDmsToVersion: async (itemId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/items/${itemId}/pull_dms_to_version`,
      {},
      dealId,
    )
    return parseWithZod(PullDmsToVersionResponseSchema, result).version
  },

  /**
   * Accepts every detected signature on the given pages. Rejections arrive as a `ClofoApiError`
   * whose `detail` is one of `max_signing_officers`, `duplicate_signatures` or `must_name_officer`
   * — each needs its own message, so callers must branch on it rather than showing a generic error.
   */
  confirmDetectedSignatures: async (itemId: number, signaturePageIds: number[], dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/items/${itemId}/confirm_detected_signatures`,
      { signature_page_ids: signaturePageIds },
      dealId,
    )
    return parseWithZod(ConfirmDetectedSignaturesResponseSchema, result)
  },

  /**
   * Runs signature detection over the given pages, creating a signature for every signatory it
   * reads off them.
   *
   * On a deal without LLM features the server stops after finding the signature *pages* when the
   * item has no signatures yet, and waits for this call to work out who signs them. Detection
   * runs in the background: the pages come back marked `is_detecting`, and the signatures
   * themselves arrive over the deal's live connection once it finishes.
   */
  detectSignatures: async (itemId: number, signaturePageIds: number[], dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/items/${itemId}/detect_signatures`,
      { signature_page_ids: signaturePageIds },
      dealId,
    )
    return parseWithZod(DetectSignaturesResponseSchema, result).signature_pages
  },

  clearSignaturePageNeedsReview: async (
    itemId: number,
    signaturePageIds: number[],
    dealId: number,
  ) => {
    const regionHost = auth.getUserRegionHost()
    await http.post(
      `${regionHost}/api/items/${itemId}/clear_signature_page_needs_review`,
      { signature_page_ids: signaturePageIds },
      dealId,
    )
  },

  /**
   * Moves every signature on the item currently in `status` back to `current`,
   * which is how the obsolete and altered review flows are resolved.
   */
  clearSignaturesStatus: async (itemId: number, status: 'obsolete' | 'altered', dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/items/${itemId}/clear_signatures_status`,
      { status },
      dealId,
    )
    return parseWithZod(z.object({ signatures: z.array(SignatureSchema) }), result).signatures
  },

  setDmsDocument: async (
    itemId: number,
    payload: {
      document_id: string
      workspace_id?: number | null
      library_id?: string
      file_size?: number
      last_user_description?: string
      version?: number
      name?: string
      extension?: string
      edit_date?: string | null
    },
    dealId: number,
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/items/${itemId}/set_dms_document`,
      payload,
      dealId,
    )
    return parseWithZod(ItemResponseSchema, result).item
  },
}
